/* eslint-disable @typescript-eslint/no-unused-vars*/
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { StatusChangeFeedbackParams } from 'clients/CampaignsClient/CampaignsClient.types';
import { queryClient } from 'config/queryClient';
import { isAxiosError } from 'helpers/clientHelpers';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useContext } from 'react';
import { CANDIDATE_STATUS } from 'services/candidates';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { SnackbarContext } from 'shared/legacy/contexts/SnackbarContext/SnackbarContext';
import { QueryKey } from 'types/query';
import { useCandidateAction, WrappedHookParams } from './useCandidateAction';

export const useHidden = (params: WrappedHookParams) => {
  const { createSnackbar } = useContext(SnackbarContext);
  const { dictionary } = useContext(LocalizationContext);

  return useCandidateAction<HiddenHookCallbackParams>(
    async ({ candidate, reasons, status_change_feedback }) => {
      await campaignsClient.createOrUpdateCampaignCandidate({
        campaignId: candidate.campaignId,
        esPersonId: candidate.es_person_id,
        status: CANDIDATE_STATUS.HIDDEN,
        is_unlocked: candidate.isUnlocked,
        ...(!!reasons && { reasons }),
        ...(!!status_change_feedback && { status_change_feedback }),
      });
    },
    {
      onError: () => {
        return async ({ candidateOrchestrator: co, data: error }) => {
          if (isAxiosError(error) && error.response?.status === 409) {
            createSnackbar(dictionary.candidateAlreadyLinkedToAnotherMatch, {
              variant: 'danger',
            });

            co.campaign.decreaseStatOf.currentJobBoard();
            co.campaign.decreaseStatOf.currentList();
            co.campaign.remove.fromCurrentList();
          }
          throw error;
        };
      },
      onSuccess: ({ fcParams: { candidate }, gtm }) => {
        gtm.hiddenCandidate(candidate.campaignId, candidate.es_person_id);
        queryClient.invalidateQueries([QueryKey.getInfiniteConversations], { exact: false });
        queryClient.invalidateQueries(
          [QueryKey.useCandidateActivityLogQuery, { candidateEsPersonId: candidate.es_person_id }],
          { exact: false },
        );
        createSnackbar(dictionary.candidateIsMovedTo(candidate.firstName, dictionary.hidden), {
          variant: 'success',
        });

        return async ({ candidateOrchestrator: co, pageContext }) => {
          if (pageContext === PageContext.Campaign) {
            if (candidate.status === CANDIDATE_STATUS.NEW) {
              co.campaign.decreaseStatOf.currentJobBoard();
            }

            co.campaign.remove.fromCurrentList();
            co.campaign.reset.hiddenList();
            co.campaign.decreaseStatOf.currentList();
            co.campaign.increaseStatOf.hiddenList();
          } else if (pageContext === PageContext.RatedCandidates) {
            co.ratedCandidates.remove.fromCurrentList();
            co.ratedCandidates.reset.hiddenList();
            co.ratedCandidates.decreaseStatOf.currentList();
            co.ratedCandidates.increaseStatOf.hiddenList();
          } else if (pageContext === PageContext.Shared || pageContext === PageContext.Candidate) {
            co.share.moveCandidateTo.hiddenList();
          }
        };
      },
    },
  )(params);
};

type HiddenHookCallbackParams = {
  reasons?: string[];
  status_change_feedback?: StatusChangeFeedbackParams;
};
